/**
 * Service de géocodage centralisé via backend
 * Utilise UNIQUEMENT les routes backend pour éviter les duplications et rate limits
 * Backend hybride: LocationIQ (priorité 1) + Nominatim (fallback)
 */

import api from './api';

class GeocodingService {
  constructor() {
    // Configuration pour logs et debugging uniquement
    this.backendUrl = '/api/v1/geocoding';
    this.userAgent = 'Krillou-App/1.0';
    
    console.log('[GeocodingService] Initialized - Backend-only approach');
    console.log('[GeocodingService] All requests will go through:', this.backendUrl);
  }

  /**
   * Gestion centralisée des erreurs avec fallbacks
   */
  handleError(error, context = '') {
    console.error(`[GeocodingService] Erreur ${context}:`, error);
    
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      return new Error('Erreur de connexion SSL ou de réseau');
    }
    
    if (error.message.includes('SSL') || error.message.includes('PROTOCOL')) {
      return new Error('Erreur de protocole SSL');
    }
    
    if (error.message.includes('CORS')) {
      return new Error('Erreur CORS');
    }
    
    return error;
  }

  /**
   * Recherche d'adresses avec autocomplétion via backend UNIQUEMENT
   * Utilise le système hybride backend (LocationIQ + Nominatim) pour éviter les rate limits
   */
  async searchAddresses(query, countryFilter = 'fr,be,ch,lu,mc,ad') {
    console.log('[GeocodingService] Using backend-only approach for:', query);
    
    try {
      // NOUVEAU: Utiliser UNIQUEMENT le backend hybride avec langue anglaise forcée
      const response = await api.get('/geocoding/search', {
        params: { 
          q: query, 
          countrycodes: countryFilter,
          limit: 5,
          'accept-language': 'en',
          language: 'en'
        },
        timeout: 10000
      });
      
      if (response.data.success) {
        const suggestions = response.data.suggestions || [];
        
        // Format compatible avec l'interface existante
        return suggestions.map(item => ({
          ...item,
          label: item.display_name,
          lat: parseFloat(item.lat || item.latitude),
          lon: parseFloat(item.lon || item.longitude),
          place_id: item.place_id || null // Inclure le place_id
        }));
      } else {
        console.warn('[GeocodingService] Backend search failed:', response.data.error);
        throw new Error(response.data.error || 'Aucun résultat trouvé');
      }
      
    } catch (error) {
      console.error('[GeocodingService] Backend search error:', error.message);
      const handledError = this.handleError(error, 'recherche adresse');
      throw handledError;
    }
  }

  /**
   * Géocodage inversé (coordonnées vers adresse) via backend UNIQUEMENT
   * Utilise le système hybride backend pour éviter les conflits
   */
  async reverseGeocode(lat, lon) {
    console.log('[GeocodingService] Using backend-only reverse geocoding for:', lat, lon);
    
    try {
      // NOUVEAU: Utiliser UNIQUEMENT le backend hybride avec langue anglaise forcée
      const response = await api.get('/geocoding/reverse', {
        params: { 
          lat, 
          lon,
          'accept-language': 'en',
          language: 'en'
        },
        timeout: 10000
      });
      
      if (response.data.success && response.data.result) {
        const result = response.data.result;
        
        return {
          display_name: result.display_name,
          address: result.address || {},
          lat: parseFloat(lat),
          lon: parseFloat(lon),
          place_id: result.place_id || null, // Inclure le place_id
          provider: result.provider,
          cached: result.cached,
          raw: result
        };
      } else {
        console.warn('[GeocodingService] Backend reverse failed:', response.data.error);
        return null;
      }
      
    } catch (error) {
      console.error('[GeocodingService] Backend reverse error:', error.message);
      const handledError = this.handleError(error, 'géocodage inversé');
      throw handledError;
    }
  }

  /**
   * Extrait et formate les composants d'adresse
   */
  formatAddressComponents(geocodeResult) {
    if (!geocodeResult || !geocodeResult.address) {
      return {
        address: geocodeResult?.display_name || '',
        lat: geocodeResult?.lat || null,
        lon: geocodeResult?.lon || null,
        place_id: geocodeResult?.place_id || null // Inclure le place_id même pour les cas simples
      };
    }

    const addr = geocodeResult.address;
    // Choisir la "localité" la plus pertinente si city est manquant (certains providers retournent municipality/suburb/etc.)
    const pickLocality = (a) => {
      const localityKeys = [
        'city',
        'town',
        'village',
        'municipality',
        'borough',
        'suburb',
        'hamlet',
        'locality',
        'city_district',
        'state_district',
        'county'
      ];
      for (const key of localityKeys) {
        if (a && a[key] && String(a[key]).trim()) return a[key];
      }
      return '';
    };
    const city = pickLocality(addr);
    
    return {
      address: geocodeResult.display_name,
      street_name: addr.road || '',
      street_number: addr.house_number || '',
      city,
      postal_code: addr.postcode || '',
      region: addr.state || addr.region || '',
      country: addr.country || '',
      country_code: addr.country_code ? addr.country_code.toUpperCase() : '',
      lat: parseFloat(geocodeResult.lat),
      lon: parseFloat(geocodeResult.lon),
      place_id: geocodeResult.place_id || null // Inclure le place_id
    };
  }

  /**
   * Fallback : génère une adresse basique à partir des coordonnées
   */
  generateFallbackAddress(lat, lon) {
    const formattedLat = parseFloat(lat).toFixed(6);
    const formattedLon = parseFloat(lon).toFixed(6);
    
    return {
      address: `${formattedLat}, ${formattedLon}`,
      street_name: '',
      street_number: '',
      city: '',
      postal_code: '',
      region: '',
      country: '',
      country_code: '',
      lat: parseFloat(lat),
      lon: parseFloat(lon),
      place_id: null, // Pas de place_id pour les coordonnées générées manuellement
      isFallback: true
    };
  }

  /**
   * Méthode wrapper avec fallback automatique pour le géocodage inversé
   */
  async reverseGeocodeWithFallback(lat, lon) {
    try {
      const result = await this.reverseGeocode(lat, lon);
      if (result) {
        const formatted = this.formatAddressComponents(result);
        return formatted;
      } else {
        return this.generateFallbackAddress(lat, lon);
      }
    } catch (error) {
      console.warn('[GeocodingService] Fallback vers coordonnées en raison de:', error.message);
      return this.generateFallbackAddress(lat, lon);
    }
  }

  /**
   * Méthode wrapper avec fallback pour la recherche d'adresses
   */
  async searchAddressesWithFallback(query, countryFilter = 'fr,be,ch,lu,mc,ad') {
    try {
      return await this.searchAddresses(query, countryFilter);
    } catch (error) {
      console.warn('[GeocodingService] Recherche échouée:', error.message);
      // Retourner un tableau vide en cas d'échec
      return [];
    }
  }
}

// Instance singleton
const geocodingService = new GeocodingService();

export default geocodingService;
